import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '../api';
import ProductCard from '../components/ProductCard';
import { FiTruck, FiRefreshCw, FiCheckCircle, FiCreditCard, FiArrowRight, FiStar, FiInstagram } from 'react-icons/fi';
import HeroCarousel from '../components/HeroCarousel';
import FlashSale from '../components/FlashSale';
import { useSiteSettings } from '../context/SiteSettingsContext';
import SEO from '../components/SEO';

const Home = () => {
  const { appearance, loading: settingsLoading } = useSiteSettings();

  
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuredRes, newRes, bestRes] = await Promise.all([
          API.get('/api/products?featured=true&limit=8'),
          API.get('/api/products?sort=newest&limit=8'),
          API.get('/api/products?sort=popular&limit=4'),
        ]);
        setFeatured(featuredRes.data.products);
        setNewArrivals(newRes.data.products);
        setBestSellers(bestRes.data.products);
      } catch (error) {
        console.error('Failed to fetch homepage data:', error);
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, []);

  const testimonials = [
    {
      name: 'Priya Sharma',
      city: 'Mumbai',
      rating: 5,
      text: 'Absolutely in love with my Milano Shoulder Bag! The leather quality is amazing and it fits everything I need. Delivery was super fast too!',
    },
    {
      name: 'Ananya Desai',
      city: 'Bangalore',
      rating: 5,
      text: 'The Gold Chain Sling Bag is exactly what I was looking for. Perfect for evening outings. The packaging was so premium — felt like receiving a gift!',
    },
    {
      name: 'Kavita Reddy',
      city: 'Hyderabad',
      rating: 4,
      text: 'Great quality products at reasonable prices. Ordered the Canvas Office Tote and it\'s been my daily work companion. Would definitely shop again!',
    },
  ];

  const sections = appearance?.sections || { featuredProducts: true, newArrivals: true, saleBanner: false, instagramFeed: true, testimonials: true };

  if (settingsLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-offwhite">
      <div className="w-12 h-12 border-4 border-gold/20 border-t-gold rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="pt-16 md:pt-20 bg-offwhite">
      <SEO
        title="Premium Ladies Bags & Accessories Store India"
        description="Poshe Styles — Shop premium ladies bags, designer handbags, clutches, totes, wallets, sling bags & backpacks online in India. Free shipping over ₹999. 100% quality guaranteed. Carry Your World in Style."
        url="/"
        keywords="Poshe Styles, PosheStyles, poshe style, poshe styles bags, premium ladies bags, ladies bags online India, designer handbags India, buy handbags online, women handbags, clutches online India, tote bags, wallets for women, sling bags, crossbody bags, backpacks for women, premium bags India, luxury bags affordable, leather bags women, office bags for women, party clutches, branded bags India, fashion accessories women, stylish handbags, trendy bags India"
      />
      <h1 className="sr-only">Poshe Styles - Premium Ladies Handbags, Clutches, and Accessories</h1>
      <HeroCarousel />
      
      <FlashSale products={featured} />

      {/* New Arrivals */}
      {sections.newArrivals && (
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="section-title text-left m-0">New Arrivals</h2>
              <p className="text-gray-500 mt-2">The latest drops from our design studio</p>
            </div>
            <Link to="/products?sort=newest" className="hidden md:flex items-center gap-2 text-gold font-bold text-sm tracking-widest uppercase">View All <FiArrowRight /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {loadingData ? (
               [...Array(8)].map((_, i) => (
                 <div key={i} className="bg-gray-100 rounded-2xl aspect-[3/4] animate-pulse"></div>
               ))
            ) : newArrivals.length > 0 ? (
               newArrivals.slice(0, 8).map((product) => (
                 <ProductCard key={product._id} product={product} />
               ))
            ) : (
               <div className="col-span-full py-12 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                  <h3 className="text-xl font-playfair text-navy mb-2">New collection coming soon...</h3>
                  <p className="text-sm text-gray-500">We're crafting something beautiful. Check back later.</p>
               </div>
            )}
          </div>
        </div>
      </section>
      )}

      {/* Brand Strip */}
      <section className="py-12 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
             {[
               { icon: <FiTruck />, title: 'Free Shipping', desc: 'Over ₹999' },
               { icon: <FiRefreshCw />, title: 'Easy Returns', desc: '7 Day Policy' },
               { icon: <FiCheckCircle />, title: 'Genuine Pride', desc: '100% Quality' },
               { icon: <FiCreditCard />, title: 'Secure Pay', desc: 'PCI Protected' }
             ].map((p, i) => (
               <div key={i} className="flex items-center gap-4 text-white">
                  <div className="p-3 bg-white/5 rounded-full text-gold text-2xl">{p.icon}</div>
                  <div>
                    <h4 className="font-bold text-sm uppercase tracking-wider">{p.title}</h4>
                    <p className="text-[10px] text-gray-400 uppercase tracking-widest">{p.desc}</p>
                  </div>
               </div>
             ))}
          </div>
        </div>
      </section>

      {/* Best Sellers */}
      {sections.featuredProducts && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
             <h2 className="section-title">Best Sellers</h2>
             <p className="section-subtitle">Our most popular designs this month</p>
             <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-12">
                {bestSellers.map(p => <ProductCard key={p._id} product={p} />)}
             </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {sections.testimonials && (
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title">Customer Stories</h2>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {testimonials.map((review, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, j) => <FiStar key={j} size={16} className="text-yellow-400 fill-yellow-400" />)}
                </div>
                <p className="text-gray-600 italic mb-6">"{review.text}"</p>
                <div className="font-bold text-navy">{review.name} <span className="text-gray-400 font-normal">| {review.city}</span></div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Instagram Feed */}
      {sections.instagramFeed && (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="section-title">Follow Us @PosheStyles</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            {[
              '/images/instagram/media__1776072899570.jpg',
              '/images/instagram/media__1776072928555.jpg',
              '/images/instagram/media__1776072984983.jpg',
              '/images/instagram/media__1776073023030.jpg'
            ].map((imgSrc, index) => (
              <div key={index} className="relative aspect-[4/5] bg-gray-100 rounded-2xl overflow-hidden group shadow-md shadow-navy/5">
                 <img src={imgSrc} alt="Poshe Styles Latest Collection on Instagram" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out" />
                 <div className="absolute inset-0 bg-navy/20 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-center items-center text-white">
                   <FiInstagram size={32} className="mb-2 drop-shadow-md" />
                   <span className="font-playfair font-bold tracking-widest text-sm drop-shadow-md">SHOP NOW</span>
                 </div>
              </div>
            ))}
          </div>
          <a href="https://instagram.com/poshe_styles" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-gold font-bold mt-10 hover:gap-3 transition-all">Instagram <FiArrowRight /></a>
        </div>
      </section>
      )}

      {/* About Section */}
      <section id="about" className="py-20 bg-offwhite">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                 <h2 className="font-playfair text-4xl font-bold text-navy mb-6">Our Story</h2>
                 <p className="text-gray-600 leading-relaxed mb-6 font-inter">Founded with a passion for exquisite craftsmanship, Poshe Styles brings premium accessories to the modern woman. Every piece in our collection is curated with an eye for detail, ensuring not just beautiful aesthetics but also enduring quality.</p>
                 <Link to="/products" className="btn-gold inline-flex">Explore Collection</Link>
              </div>
              <div className="relative">
                 <img src="https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800" alt="Poshe Styles Premium Accessories Collection Story" className="rounded-2xl shadow-2xl" />
                 <div className="absolute -bottom-8 -left-8 bg-gold text-white p-8 rounded-2xl hidden lg:block shadow-xl">
                    <p className="font-playfair text-2xl italic">"Style is a way to say who you are without having to speak."</p>
                 </div>
              </div>
           </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
